import dayjs from "dayjs";
import { MARKET_LABELS, API } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import Odds from "@/components/Odds";
import { UnitsHeading } from "@/components/UnitsHeading";

export const RESULT_FILTERS = [
  { key: "", label: "All" },
  { key: "win", label: "Wins" },
  { key: "loss", label: "Losses" },
  { key: "push", label: "Pushes" },
];

export const MARKET_FILTERS = [
  { key: "", label: "All Markets" },
  { key: "player_prop", label: "Player Props" },
  { key: "spread", label: "Spreads" },
  { key: "moneyline", label: "Moneylines" },
  { key: "total", label: "Totals" },
];

const SKELETON_ROWS = ["r1", "r2", "r3", "r4", "r5", "r6", "r7", "r8"];

const profitCls = (p) => (p > 0 ? "text-[#00E676]" : p < 0 ? "text-[#EF4444]" : "text-slate-400");
const fmtProfit = (p) => `${p > 0 ? "+" : ""}${p.toFixed(2)}U`;

function LedgerFilters({ result, setResult, market, setMarket }) {
  return (
    <div className="flex flex-wrap gap-2">
      <div className="flex gap-1.5" role="group" aria-label="Result filter">
        {RESULT_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-result-${f.key || "all"}`}
            onClick={() => setResult(f.key)}
            className={`rounded-md border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors ${
              result === f.key ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-[#2A364F] text-slate-400 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <select
        data-testid="filter-market-select"
        value={market}
        onChange={(e) => setMarket(e.target.value)}
        className="rounded-md border border-[#2A364F] bg-[#0F131D] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-slate-300 outline-none focus:border-[#00E676]"
      >
        {MARKET_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
    </div>
  );
}

function LedgerRow({ r }) {
  const hist = r.source === "historical_import";
  const pendingShot = hist && r.evidence?.some((e) => e.shows_status !== "settled");
  return (
    <tr className="border-b border-[#1E2738]/60 transition-colors hover:bg-[#1A2234]/50" data-testid={`results-row-${r.id}`}>
      <td className="px-5 py-3.5 font-mono text-xs text-slate-400">{dayjs(r.date).format("MMM D")}{hist && r.date_basis === "event" && <span className="ml-1 text-slate-600" title="Game date — bet placement time not established">*</span>}</td>
      <td className="px-5 py-3.5 font-semibold text-white">
        {r.pick}
        {r.pick_type === "multi" && <span className="ml-2 font-mono text-[10px] text-slate-500">{r.legs_count || r.legs?.length} legs</span>}
        {r.visibility === "vip" && !hist && <span className="ml-2 tag bg-[#F59E0B]/15 text-[#F59E0B]">VIP</span>}
        {hist && r.shared === "personal" && <span className="ml-2 tag border border-slate-500/40 text-slate-400">Personal</span>}
        {hist && r.evidence?.length > 0 && <a href={`${API}/evidence/${r.evidence[0].file}`} target="_blank" rel="noopener noreferrer" className="ml-2 font-mono text-[10px] text-[#00E676] underline-offset-2 hover:underline" data-testid={`evidence-link-${r.id}`}>Slip</a>}
        {hist && <span className="ml-2 font-mono text-[10px] text-slate-500" title="Recorded manually from the owner's betting account; not auto-verified from box scores" data-testid={`manual-record-note-${r.id}`}>manually recorded</span>}
        {pendingShot && <span className="ml-2 font-mono text-[10px] text-[#F59E0B]" title="Screenshot showed pending/proposed status; settlement confirmed by the account owner">screenshot pre-settlement</span>}
        {r.corrected && <span className="ml-2 tag border border-[#F59E0B]/40 text-[#F59E0B]" title="Result corrected — audit trail preserved" data-testid={`corrected-badge-${r.id}`}>Corrected</span>}
      </td>
      <td className="px-5 py-3.5 text-slate-400">{MARKET_LABELS[r.market] || (r.pick_type === "multi" ? "Multi" : r.market)}</td>
      <td className="px-5 py-3.5 font-mono text-slate-300"><Odds value={r.odds} /></td>
      <td className="px-5 py-3.5 font-mono text-slate-300">{r.units}U{r.stake_aud != null && <span className="block text-[10px] text-slate-500">${r.stake_aud}</span>}</td>
      <td className="px-5 py-3.5"><StatusBadge status={r.result} /></td>
      <td className={`px-5 py-3.5 text-right font-mono font-bold ${profitCls(r.profit)}`}>{fmtProfit(r.profit)}{r.net_aud != null && <span className="block text-[10px] font-normal opacity-80">{r.net_aud > 0 ? "+" : r.net_aud < 0 ? "−" : ""}${Math.abs(r.net_aud).toFixed(2)}</span>}</td>
    </tr>
  );
}

export function LedgerTable({ rows, isLoading, isFetching, filters }) {
  return (
    <div className="card-mp mt-8 overflow-hidden" data-testid="results-ledger-table">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#1E2738] p-5">
        <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Recent Results</h3>
        <LedgerFilters {...filters} />
      </div>
      <div className={`overflow-x-auto ${isFetching ? "opacity-70" : ""}`}>
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#1E2738] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4">Pick</th>
              <th className="px-5 py-4">Market</th>
              <th className="px-5 py-4">Odds</th>
              <th className="px-5 py-4"><UnitsHeading /></th>
              <th className="px-5 py-4">Result</th>
              <th className="px-5 py-4 text-right">Profit/Loss</th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? SKELETON_ROWS.map((k) => (
                  <tr key={k} className="border-b border-[#1E2738]/60">
                    <td colSpan={7} className="px-5 py-4"><Skeleton className="h-4 w-full bg-[#1E2738]" /></td>
                  </tr>
                ))
              : (rows || []).map((r) => <LedgerRow key={r.id} r={r} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
